import React from 'react';
import { FaCheck } from 'react-icons/fa';
import { getStaticContent } from '../constants/staticContent';

const AboutSimple = ({ language = 'en' }) => {
  const { ABOUT_SIMPLE } = getStaticContent(language);
  return (
    <section id="about" className="min-h-screen flex items-center bg-gray-50 py-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto w-full">
        <style>{`
          @keyframes slideInLeft {
            from { opacity: 0; transform: translateX(-30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .slide-left { animation: slideInLeft 0.8s ease-out both; }
          .slide-right { animation: slideInRight 0.8s ease-out both; }
          .fade-in { animation: fadeIn 0.8s ease-out both; }

          .stat-divider {
            border-right: 1px solid #E5E7EB;
          }
          .stat-divider:last-child {
            border-right: none;
          }
          @media (max-width: 768px) {
            .stat-divider {
              border-right: none;
              border-bottom: 1px solid #E5E7EB;
              padding-bottom: 24px;
              margin-bottom: 24px;
            }
            .stat-divider:last-child {
              border-bottom: none;
              padding-bottom: 0;
              margin-bottom: 0;
            }
          }
        `}</style>

        {/* Header */}
        <div className="text-center mb-16 md:mb-20 fade-in">
          <div className="inline-block mb-4">
            <span
              className="text-xs font-bold tracking-widest uppercase text-emerald-700 bg-emerald-50 px-4 py-1.5 rounded-full border border-emerald-200"
            >
              {ABOUT_SIMPLE.sectionLabel}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6">
            {ABOUT_SIMPLE.sectionTitle}
          </h2>
        </div>

        {/* Mission & Motive Cards */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-10 mb-16 md:mb-20">
          {/* Our Motive */}
          <div
            className="slide-left bg-white rounded-2xl p-8 md:p-10 transition-all duration-300"
            style={{
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
            }}
          >
            <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-red-50 rounded-2xl mb-5 text-2xl md:text-3xl">🎯</div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
              {ABOUT_SIMPLE.motiveTitle}
            </h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
              {ABOUT_SIMPLE.motiveDescription}
            </p>
            <div className="space-y-3">
              {ABOUT_SIMPLE.motiveBullets.map((bullet, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <FaCheck className="text-emerald-600 flex-shrink-0 mt-1" />
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">{bullet}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Our Mission */}
          <div
            className="slide-right bg-white rounded-2xl p-8 md:p-10 transition-all duration-300"
            style={{
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
            }}
          >
            <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-emerald-50 rounded-2xl mb-5 text-2xl md:text-3xl">🚀</div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
              {ABOUT_SIMPLE.sectionTitle}
            </h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
              {ABOUT_SIMPLE.description}
            </p>
            <div className="bg-emerald-50/50 rounded-xl p-5 border border-emerald-100">
              <p className="text-sm md:text-base text-emerald-900 leading-relaxed italic font-medium">
                "{ABOUT_SIMPLE.vision}"
              </p>
            </div>
          </div>
        </div>

        {/* Domain Expertise */}
        <div
          className="bg-white rounded-2xl p-8 md:p-10 mb-16 md:mb-20 fade-in"
          style={{
            animationDelay: '0.3s',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
          }}
        >
          <div className="text-center mb-10">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
              {ABOUT_SIMPLE.expertiseTitle}
            </h3>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
              {ABOUT_SIMPLE.expertiseDescription}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ABOUT_SIMPLE.expertise.map((item, idx) => {
              const bg = ['bg-blue-50', 'bg-emerald-50', 'bg-purple-50'][idx];
              const color = ['text-blue-500', 'text-emerald-500', 'text-purple-500'][idx];
              return (
                <div key={idx} className="bg-white rounded-xl p-6 border border-gray-100 text-center shadow-sm">
                  <div className={`w-12 h-12 md:w-16 md:h-16 flex items-center justify-center ${bg} rounded-2xl mb-3 mx-auto`}>
                    <item.icon className={`${color} w-6 h-6`} />
                  </div>
                  <div className="text-base font-bold text-gray-900 mb-1">{item.label}</div>
                  <div className="text-sm md:text-base text-gray-600 leading-relaxed">{item.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div
          className="bg-white rounded-2xl py-10 px-6 md:px-10 fade-in flex flex-col md:flex-row shadow-sm"
          style={{
            animationDelay: '0.5s',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
          }}
        >
          {ABOUT_SIMPLE.stats.map((stat, index) => (
            <div
              key={index}
              className="flex-1 text-center stat-divider"
            >
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-emerald-600 mb-2">
                {stat.value}
              </div>
              <div className="text-sm font-bold tracking-widest uppercase text-gray-600">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Impact Statement */}
        <div
          className="mt-16 md:mt-24 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-10 md:p-14 max-w-5xl mx-auto shadow-xl text-white fade-in"
          style={{ animationDelay: '0.8s' }}
        >
          <div className="text-center">
            <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-emerald-500 rounded-2xl mb-6 mx-auto text-2xl md:text-3xl shadow-sm">🌾</div>
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              {ABOUT_SIMPLE.impactTitle}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-emerald-50">
              {ABOUT_SIMPLE.impactDescription}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSimple;
